import { useRef, useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Eraser, FileDown, Camera, CheckCircle2, ImagePlus, FolderOpen } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

// Reiche WP-Abnahme-Vorlage (Checkliste + alle Foto-Slots) — wird für `abnahme` UND `montagetermin` genutzt
const WP_ABNAHME_TEMPLATE = {
  title: "Abnahmeprotokoll — Wärmepumpe",
  sections: [
    {
      heading: "Unsere Checkliste zu Ihrer neuen Wärmepumpe",
      fields: [
        { key: "einweisung", label: "Ich habe nach der Inbetriebnahme eine Einweisung in den Gebrauch der Wärmepumpe erhalten.", type: "toggle" },
        { key: "laeuft_stoerungsfrei", label: "Meine Wärmepumpe läuft ohne Störungen.", type: "toggle" },
        { key: "warmwasser_ok", label: "Mein Wasser wird warm.", type: "toggle" },
        { key: "heizung_warm", label: "Meine Heizung wird warm.", type: "toggle" },
        { key: "vdz_formular_erhalten", label: "Ich habe ein ausgefülltes VdZ-Formular (Nachweis über hydraulischen Abgleich) erhalten.", type: "toggle" },
        { key: "aufraeumen", label: "Auf der Baustelle wurde alles ordnungsgemäß aufgeräumt oder ggf. für die Entsorgung zusammengeräumt.", type: "toggle" },
      ],
    },
    {
      heading: "Inbetriebnahme & Übergabe",
      fields: [
        { key: "inbetriebnahme_datum", label: "Inbetriebnahmedatum", type: "date" },
        { key: "inbetriebnahme_durch", label: "Inbetriebnahme durchgeführt von", type: "text" },
        { key: "seriennummer_aussen", label: "Seriennummer Außeneinheit", type: "text" },
        { key: "offene_aufgaben", label: "Weitere Notizen / Offene Aufgaben", type: "textarea" },
      ],
    },
  ],
  photoSlots: {
    "Komponenten": [
      "Speicher (Kombi-/Brauchwasser-/Pufferspeicher)",
      "Außeneinheit",
      "Typenschild Außeneinheit",
      "Inneneinheit",
      "Pumpengruppen",
    ],
    "Gesamtbild & Dokumentation": [
      "Gesamtansicht Anlage",
      "VdZ-Formular",
      "Gesamtansicht Wärmepumpe mit Haus sichtbar",
    ],
    "Weitere Bilder": [
      "Weiteres Bild 1",
      "Weiteres Bild 2",
      "Weiteres Bild 3",
    ],
  },
};

// Feld-Konfiguration je Phase — angelehnt an DC-Abnahmeprotokoll-Vorlage
export const PROTOKOLL_TEMPLATES = {
  dc_montage: {
    title: "Abnahmeprotokoll — DC-Montage (Dach)",
    sections: [
      {
        heading: "Prüfpunkte Montage",
        fields: [
          { key: "anzahl_module", label: "Anzahl Module", type: "number" },
          { key: "gesamtleistung_kwp", label: "Gesamtleistung in kWp", type: "number" },
          { key: "dachdurchdringung", label: "Dachdurchdringung in Ordnung?", type: "toggle" },
          { key: "montagesystem", label: "Montagesystem in Ordnung?", type: "toggle" },
          { key: "verkabelung", label: "Verkabelung / Leitungsführung in Ordnung?", type: "toggle" },
        ],
      },
      {
        heading: "Messungen",
        fields: [
          { key: "stringmesswerte", label: "Stringmesswerte im Toleranzbereich?", type: "toggle" },
          { key: "sonstige_messungen", label: "Sonstige Messungen", type: "textarea" },
        ],
      },
      {
        heading: "Abschluss",
        fields: [
          { key: "alle_bestandteile", label: "Ist die Installation aller DC-Bestandteile erfolgreich abgeschlossen?", type: "toggle" },
          { key: "arbeitsplatz_sauber", label: "Arbeitsplatz gereinigt / sauber hinterlassen?", type: "toggle" },
          { key: "offene_aufgaben", label: "Offene Aufgaben / Bemerkungen", type: "textarea" },
        ],
      },
    ],
    photoSlots: {
      "Installation außen": [
        "Dachkonstruktion (ohne Module)",
        "Dachhaken Nahaufnahme inkl. Abstand zu Ziegel",
        "Hauseinführung Kabel",
        "Potentialausgleich / Erdungsspieß",
        "PV-Anlage (Dach) Gesamtansicht",
      ],
    },
  },
  ac_montage: {
    title: "Abnahmeprotokoll — AC-Montage (Elektro)",
    sections: [
      {
        heading: "Prüfpunkte Elektro",
        fields: [
          { key: "wechselrichter_funktion", label: "Wechselrichter und -funktion in Ordnung?", type: "toggle" },
          { key: "einspeisekontrolle", label: "Einspeisekontrolle am Zähler in Ordnung?", type: "toggle" },
          { key: "smart_meter", label: "Smart Meter und WiFi-Stick installiert?", type: "toggle" },
        ],
      },
      {
        heading: "Zähler & Messungen",
        fields: [
          { key: "zaehlernummer", label: "Zählernummer (aktueller Zähler)", type: "text" },
          { key: "zaehlerabmeldung", label: "Zählerabmeldung?", type: "toggle" },
          { key: "zaehlernummer_abgemeldet", label: "Zählernummer (abgemeldeter Zähler)", type: "text", dependsOn: { key: "zaehlerabmeldung", value: true } },
          { key: "zaehlerstand_einspeisung", label: "Zählerstand Einspeisung in kWh", type: "number" },
          { key: "zaehlerstand_bezug", label: "Zählerstand Bezug in kWh", type: "number" },
        ],
      },
      {
        heading: "Inbetriebnahme",
        fields: [
          { key: "inbetriebnahme_erfolgt", label: "Inbetriebnahme erfolgreich durchgeführt?", type: "toggle" },
          { key: "inbetriebnahme_datum", label: "Wann wurde die Inbetriebnahme durchgeführt?", type: "date" },
          { key: "inbetriebnahme_durch", label: "Durch wen wurde die Inbetriebnahme durchgeführt?", type: "text" },
          { key: "funktionstuechtig", label: "Ist die Anlage voll funktionstüchtig?", type: "toggle" },
          { key: "nulleinspeisung_hinweis", label: "Die Anlage ist ausgeschaltet bzw. auf Nulleinspeisung eingestellt bis zum Zähleraustausch. Der Kunde wurde darauf hingewiesen.", type: "toggle" },
        ],
      },
      {
        heading: "Übergabe an Kunden",
        fields: [
          { key: "anleitungen_uebergeben", label: "Alle nötigen Anleitungen übergeben?", type: "toggle" },
          { key: "kunde_eingewiesen", label: "Kunde in das PV-System und die PV-App eingewiesen?", type: "toggle" },
          { key: "arbeitsplatz_sauber", label: "Arbeitsplatz gereinigt / sauber hinterlassen?", type: "toggle" },
          { key: "offene_aufgaben", label: "Offene Aufgaben / Bemerkungen", type: "textarea" },
        ],
      },
    ],
    photoSlots: {
      "Wechselrichter & Zählerschrank": [
        "Typenschild Wechselrichter",
        "Wechselrichter Raumansicht",
        "Zählerschrank offen Gesamtansicht",
        "Zählerschrank Anschlüsse OHNE Abdeckung (Verkabelung sichtbar)",
        "Smart Meter / WiFi Stick",
      ],
    },
  },
  abnahme: WP_ABNAHME_TEMPLATE,
  montagetermin: WP_ABNAHME_TEMPLATE,
};

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center h-6 w-11 rounded-full transition-colors shrink-0 ${checked ? "bg-primary" : "bg-muted border border-border"}`}
      data-testid={testid}
      aria-pressed={checked}
    >
      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

export function ProtokollDialog({ open, onOpenChange, projectId, phaseKey, kundeName, onDone }) {
  const template = PROTOKOLL_TEMPLATES[phaseKey] || PROTOKOLL_TEMPLATES.abnahme;
  const canvasRef = useRef(null);
  const [name, setName] = useState("");
  const [fields, setFields] = useState({});
  const [slotPhotos, setSlotPhotos] = useState({}); // slotLabel → { file, previewUrl }
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [phasePhotos, setPhasePhotos] = useState([]);
  const [pickerSlot, setPickerSlot] = useState(null);

  useEffect(() => {
    if (!open || !projectId) return;
    setPickerSlot(null);
    api.get(`/projects/${projectId}`)
      .then((r) => {
        const ph = (r.data.phases || []).find((x) => x.key === phaseKey);
        setPhasePhotos((ph?.photos || []).filter((p) => p.path && !p.path.endsWith(".pdf")));
      })
      .catch(() => setPhasePhotos([]));
  }, [open, projectId, phaseKey]);

  const photoUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/documents/${p.path}`;
  const toggleExisting = (slotLabel, photo) => {
    setSlotPhotos((s) => {
      const list = s[slotLabel] || [];
      const has = list.some((x) => x.existing?.id === photo.id);
      const nextList = has ? list.filter((x) => x.existing?.id !== photo.id)
        : [...list, { id: `ex-${photo.id}`, existing: photo, previewUrl: photoUrl(photo) }];
      const next = { ...s };
      if (nextList.length) next[slotLabel] = nextList; else delete next[slotLabel];
      return next;
    });
  };

  useEffect(() => {
    if (!open) return;
    setName(kundeName || "");
    setFields({});
    setSlotPhotos({});
    setHasDrawn(false);
    setTimeout(() => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.strokeStyle = "#111";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
    }, 60);
  }, [open, kundeName, phaseKey]);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - r.left;
    const y = (t ? t.clientY : e.clientY) - r.top;
    return { x: x * (c.width / r.width), y: y * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); setDrawing(true); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); setHasDrawn(true); };
  const end = () => setDrawing(false);
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const pickPhotos = (slotLabel, files) => {
    const list = Array.from(files || []).filter((f) => f && f.type?.startsWith("image/"));
    if (!list.length) return;
    const entries = list.map((file) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, previewUrl: URL.createObjectURL(file) }));
    setSlotPhotos((p) => ({ ...p, [slotLabel]: [...(p[slotLabel] || []), ...entries] }));
  };

  const removePhoto = (slotLabel, entryId) => {
    setSlotPhotos((p) => {
      const list = p[slotLabel] || [];
      const gone = list.find((x) => x.id === entryId);
      if (gone?.previewUrl && !gone.existing) URL.revokeObjectURL(gone.previewUrl);
      const rest = list.filter((x) => x.id !== entryId);
      const next = { ...p };
      if (rest.length) next[slotLabel] = rest; else delete next[slotLabel];
      return next;
    });
  };

  const filledCount = useMemo(() => Object.values(slotPhotos).reduce((n, l) => n + l.length, 0), [slotPhotos]);
  const totalSlots = useMemo(() => Object.values(template.photoSlots || {}).flat().length, [template]);

  const submit = async () => {
    if (!hasDrawn) { toast.error("Bitte Unterschrift eingeben"); return; }
    setBusy(true);
    try {
      // Fotos vorher hochladen — jede in die Phase
      const uploadedSlots = [];
      const all = Object.entries(slotPhotos).flatMap(([slotLabel, list]) => list.map((entry) => [slotLabel, entry]));
      for (const [slotLabel, entry] of all) {
        if (entry.existing) {
          uploadedSlots.push({ slot: slotLabel, photo_id: entry.existing.id, path: entry.existing.path, name: entry.existing.name });
          continue;
        }
        try {
          const fd = new FormData();
          fd.append("file", entry.file);
          const { data } = await api.post(`/projects/${projectId}/phases/${phaseKey}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploadedSlots.push({ slot: slotLabel, photo_id: data.id, path: data.path, name: data.name });
        } catch (e) {
          // Weiter, aber User informieren
          toast.error(`Foto „${slotLabel}" fehlgeschlagen`);
        }
      }
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const res = await api.post(
        `/projects/${projectId}/phases/${phaseKey}/protokoll`,
        { signature: dataUrl, unterschrift_name: name, fields, slot_photos: uploadedSlots },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `Abnahmeprotokoll_${(name || "kunde").replace(/[^a-z0-9-_]/gi, "_")}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
      toast.success("Protokoll erstellt & Phase auf ‚Abgenommen' gesetzt");
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "PDF-Erstellung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="protokoll-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-2xl">{template.title}</DialogTitle>
          <DialogDescription className="sr-only">Strukturiertes Abnahmeprotokoll mit Prüfpunkten, Fotos und Unterschrift</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Prüfpunkt-Sektionen */}
          {template.sections.map((section, si) => (
            <div key={si} className="space-y-3" data-testid={`protokoll-section-${si}`}>
              <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground border-b border-border pb-1">
                {section.heading}
              </div>
              <div className="space-y-2.5">
                {section.fields.map((f) => {
                  // Bedingtes Feld: nur rendern wenn dependsOn erfüllt
                  if (f.dependsOn && fields[f.dependsOn.key] !== f.dependsOn.value) return null;
                  const val = fields[f.key];
                  if (f.type === "toggle") {
                    return (
                      <div key={f.key} className="flex items-center justify-between gap-3 py-1">
                        <Label className="text-sm cursor-pointer flex-1" onClick={() => setField(f.key, !val)}>{f.label}</Label>
                        <Toggle checked={!!val} onChange={(v) => setField(f.key, v)} testid={`protokoll-field-${f.key}`} />
                      </div>
                    );
                  }
                  if (f.type === "textarea") {
                    return (
                      <div key={f.key} className="space-y-1">
                        <Label className="text-sm">{f.label}</Label>
                        <Textarea rows={2} value={val || ""} onChange={(e) => setField(f.key, e.target.value)} data-testid={`protokoll-field-${f.key}`} />
                      </div>
                    );
                  }
                  return (
                    <div key={f.key} className="grid grid-cols-2 items-center gap-3">
                      <Label className="text-sm">{f.label}</Label>
                      <Input
                        type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                        step={f.type === "number" ? "0.01" : undefined}
                        value={val || ""}
                        onChange={(e) => setField(f.key, e.target.value)}
                        data-testid={`protokoll-field-${f.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Foto-Slots */}
          {Object.entries({ ...(template.photoSlots || {}), ...(Object.keys(template.photoSlots || {}).some((g) => /weitere/i.test(g)) ? {} : { "Weitere Fotos": ["Weitere Fotos"] }) }).map(([group, slots]) => (
            <div key={group} className="space-y-3" data-testid={`protokoll-photogroup-${group}`}>
              <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground border-b border-border pb-1 flex items-center justify-between">
                <span>{group}</span>
                <span className="text-[10px] font-normal text-muted-foreground">
                  {slots.reduce((n, s) => n + (slotPhotos[s]?.length || 0), 0)} Foto(s)
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {slots.map((slot) => {
                  const list = slotPhotos[slot] || [];
                  return (
                    <div
                      key={slot}
                      className={`rounded-md border border-dashed p-2 text-xs transition-colors space-y-2 ${list.length ? "border-primary bg-primary/5" : "border-border"}`}
                      data-testid={`protokoll-slot-${slot}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{slot}</div>
                          <div className="text-[10px] text-muted-foreground truncate" data-testid={`protokoll-slot-count-${slot}`}>
                            {list.length ? `${list.length} Foto(s) ausgewählt` : "Kamera oder Galerie (mehrere möglich)"}
                          </div>
                        </div>
                        <label className="h-9 w-9 rounded-md border border-border flex items-center justify-center cursor-pointer hover:bg-muted shrink-0" title="Foto aufnehmen" data-testid={`protokoll-slot-camera-${slot}`}>
                          <Camera className="h-4 w-4" />
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            capture="environment"
                            className="hidden"
                            data-testid={`protokoll-slot-camera-input-${slot}`}
                            onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; pickPhotos(slot, fs); }}
                          />
                        </label>
                        <label className="h-9 w-9 rounded-md border border-border flex items-center justify-center cursor-pointer hover:bg-muted shrink-0" title="Aus Galerie wählen (mehrere)" data-testid={`protokoll-slot-gallery-${slot}`}>
                          <ImagePlus className="h-4 w-4" />
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            className="hidden"
                            data-testid={`protokoll-slot-gallery-input-${slot}`}
                            onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; pickPhotos(slot, fs); }}
                          />
                        </label>
                        {phasePhotos.length > 0 && (
                          <button type="button" onClick={() => setPickerSlot(pickerSlot === slot ? null : slot)}
                            className={`h-9 px-2 rounded-md border text-[10px] font-medium shrink-0 transition-colors ${pickerSlot === slot ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted"}`}
                            title="Schon hochgeladene Fotos des Einsatzes wählen" data-testid={`protokoll-slot-existing-${slot}`}>
                            <FolderOpen className="h-4 w-4 inline" /> Einsatz
                          </button>
                        )}
                      </div>
                      {pickerSlot === slot && (
                        <div className="rounded border border-border bg-background p-2 space-y-1.5" data-testid={`protokoll-existing-picker-${slot}`}>
                          <div className="text-[10px] text-muted-foreground">Fotos dieses Einsatzes antippen zum Hinzufügen/Entfernen</div>
                          <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5 max-h-48 overflow-y-auto">
                            {phasePhotos.map((p) => {
                              const chosen = list.some((x) => x.existing?.id === p.id);
                              return (
                                <button key={p.id} type="button" onClick={() => toggleExisting(slot, p)}
                                  className={`relative aspect-square !min-h-0 rounded overflow-hidden border-2 ${chosen ? "border-primary" : "border-transparent"}`}
                                  data-testid={`protokoll-existing-${slot}-${p.id}`}>
                                  <img src={photoUrl(p)} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                                  {chosen && <CheckCircle2 className="absolute top-0.5 right-0.5 h-4 w-4 text-primary bg-white rounded-full" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {list.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {list.map((entry, i) => (
                            <div key={entry.id} className="relative w-14 h-14 rounded overflow-hidden bg-muted" data-testid={`protokoll-slot-thumb-${slot}-${i}`}>
                              <img src={entry.previewUrl} alt={`${slot} ${i + 1}`} className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removePhoto(slot, entry.id)}
                                className="absolute top-0 right-0 h-5 w-5 !min-h-0 rounded-bl bg-black/60 text-white text-[11px] leading-none"
                                aria-label="Foto entfernen"
                                data-testid={`protokoll-slot-remove-${slot}-${i}`}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Unterschrift */}
          <div className="space-y-3">
            <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground border-b border-border pb-1">Unterschrift Kunde</div>
            <div className="space-y-1.5">
              <Label>Name Kunde (für Unterschrift)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="protokoll-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Unterschrift <span className="text-xs text-muted-foreground">(mit Finger/Stift zeichnen)</span></Label>
              <div className="rounded-md border border-border bg-white overflow-hidden touch-none">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="w-full block cursor-crosshair"
                  style={{ touchAction: "none" }}
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                  data-testid="signature-canvas"
                />
              </div>
              <div className="flex justify-end">
                <Button type="button" size="sm" variant="ghost" onClick={clear} data-testid="signature-clear">
                  <Eraser className="h-3 w-3 mr-1" /> Löschen
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <div className="text-xs text-muted-foreground mr-auto flex items-center gap-1.5">
            <CheckCircle2 className={`h-3.5 w-3.5 ${hasDrawn ? "text-primary" : "text-muted-foreground/40"}`} />
            <span data-testid="protokoll-photo-total">{filledCount} Foto(s)</span> · {Object.keys(slotPhotos).filter((s) => s !== "Weitere Fotos").length}/{totalSlots} Positionen · {hasDrawn ? "Unterschrieben" : "Unterschrift fehlt"}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy} data-testid="protokoll-submit">
            <FileDown className="h-4 w-4 mr-2" /> {busy ? "Erstelle …" : "Protokoll als PDF speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
